// src/components/FinancialIntelligence/FIBottomPanel.js
//
// Unified bottom panel for Financial Intelligence.
// Triggered by filter icon left of input — matches Insights filter panel pattern.
// Top section: Report Template pills (Start an Analysis)
// Bottom section: Search + Sort (Filter History)

import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Chip, TextField, Button,
  InputAdornment, IconButton, FormControl, InputLabel,
  Select, MenuItem, Collapse, Alert, Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  BookmarkBorder as SaveIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import InfoIcon from '@mui/icons-material/Info';
import { db } from '../../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

const REPORT_TEMPLATES = [
  {
    key:    'cash_flow_brief',
    label:  'Cash Flow Brief',
    prompt: 'Analyze the current cash position — runway, key drivers, immediate risks, and recommended actions.',
  },
  {
    key:    'board_package',
    label:  'Board Package',
    prompt: 'Generate a board-ready financial package with performance overview, key drivers, risks, and forward outlook.',
  },
  {
    key:    'understand_finances',
    label:  'Financial Health Diagnostic',
    prompt: 'Give me a complete financial diagnostic — strengths, pressure points, trend direction, and what I should prioritize.',
  },
  {
    key:    'decision_support',
    label:  'Decision Support',
    prompt: 'Help me evaluate a major financial decision for this client. Here is the situation:\n',
  },
  {
    key:    'cash_flow_deep_dive',
    label:  'Cash Flow Deep Dive',
    prompt: 'Analyze the current cash flow in detail — runway, AR aging, collections risk, cash conversion pressure, and the most important short-term actions.',
  },
  {
    key:    'investor_readiness',
    label:  'Investor Readiness',
    prompt: 'Assess how ready this company is for a fundraising or investor conversation — strengths of the story, gaps and red flags, missing data, and preparation priorities.',
  },
  {
    key:    'forecast_intelligence',
    label:  'Forecast Intelligence',
    prompt: 'Evaluate the forward-looking financial trajectory — direction, key variables, upside and downside scenarios, and what to watch over the next 6-12 months.',
  },
  {
    key:    'margin_pricing',
    label:  'Margin & Pricing',
    prompt: 'Analyze where margin is being compressed, whether pricing power exists to offset it, and the highest-leverage levers to improve margin.',
  },
  {
    key:    'variance_brief',
    label:  'Variance Brief',
    prompt: 'Analyze budget vs actual performance — surface the meaningful signal, distinguish structural from one-time variances, and frame the management narrative.',
  },
];

const FIBottomPanel = ({
  open,
  onTemplateSelect,
  searchQuery,
  onSearchChange,
  sortOption,
  onSortChange,
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  onClearDates,
  showAllHistory,
  onToggleShowAllHistory,
  filteredCount,
  disabled,
  user,
  currentPrompt,
}) => {
  const [localSearch, setLocalSearch] = useState(searchQuery || '');
  const [showAnalysisInfo, setShowAnalysisInfo] = useState(false);
  const [showFilterInfo, setShowFilterInfo] = useState(false);
  const [showSavedInfo, setShowSavedInfo] = useState(false);
  const [savedPrompts, setSavedPrompts] = useState([]);
  const [saveLabel, setSaveLabel] = useState('');
  const [showSaveInput, setShowSaveInput] = useState(false);

  useEffect(() => {
    if (!open || !user?.uid) return;

    const loadSaved = async () => {
      try {
        const userSnap = await getDoc(doc(db, 'users', user.uid));
        if (userSnap.exists()) {
          setSavedPrompts(userSnap.data().savedFIPrompts || []);
        } else {
          setSavedPrompts([]);
        }
      } catch (err) {
        console.error('[FIBottomPanel] Failed to load saved prompts:', err);
        setSavedPrompts([]);
      }
    };

    loadSaved();
  }, [open, user?.uid]);

  const handleSavePrompt = async () => {
    if (!user?.uid || !currentPrompt?.trim() || !saveLabel.trim()) return;

    const newEntry = {
      label: saveLabel.trim(),
      prompt: currentPrompt.trim(),
      savedAt: new Date().toISOString(),
    };

    try {
      const userRef = doc(db, 'users', user.uid);
      const nextSavedPrompts = [...savedPrompts, newEntry];

      await setDoc(userRef, { savedFIPrompts: nextSavedPrompts }, { merge: true });

      setSavedPrompts(nextSavedPrompts);
      setSaveLabel('');
      setShowSaveInput(false);
    } catch (err) {
      console.error('[FIBottomPanel] Failed to save prompt:', err);
    }
  };

  const handleDeletePrompt = async (entry) => {
    if (!user?.uid) return;

    try {
      const userRef = doc(db, 'users', user.uid);
      const nextSavedPrompts = savedPrompts.filter(p => p.savedAt !== entry.savedAt);

      await setDoc(userRef, { savedFIPrompts: nextSavedPrompts }, { merge: true });

      setSavedPrompts(nextSavedPrompts);
    } catch (err) {
      console.error('[FIBottomPanel] Failed to delete saved prompt:', err);
    }
  };

useEffect(() => {
  setLocalSearch(searchQuery || '');
}, [searchQuery]);

  const handleSearch = () => { onSearchChange(localSearch); };
  const handleClear  = () => { setLocalSearch(''); onSearchChange(''); };
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); handleSearch(); }
  };

  if (!open) return null;

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'absolute',
        bottom: 'calc(100% + 4px)',
        left: 0,
        right: 0,
        zIndex: 20,
        borderTop: '2px solid',
        borderColor: 'primary.main',
        p: 1.5,
      }}
    >
      {/* ── Start an Analysis ── */}
      <Box display="flex" alignItems="center" gap={0.5} sx={{ mb: 0.75 }}>
        <Typography variant="caption" fontWeight={600} color="text.secondary"
          sx={{ textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.06em' }}>
          Start an Analysis
        </Typography>
        <IconButton size="small" onClick={() => setShowAnalysisInfo(prev => !prev)} sx={{ p: 0.25 }}>
          <InfoIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
        </IconButton>
      </Box>
      <Collapse in={showAnalysisInfo}>
        <Alert severity="info" sx={{ mb: 1, py: 0.5 }} onClose={() => setShowAnalysisInfo(false)}>
          <Typography variant="body2" sx={{ mb: 0.75 }}>
            <strong>Report templates are starting points, not endpoints.</strong> Click any template to pre-fill the input with a structured prompt — then edit it before running. Every template is designed to be customized to your client's specific situation.
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.75 }}>
            <strong>Freeform works too.</strong> You can type any financial question directly without selecting a template. The system automatically routes your question to the most relevant analytical framework.
          </Typography>
          <Typography variant="body2">
            <strong>This is designed to be customized — that's where the real value is.</strong> Over time, fCFOs develop their own repeatable advisory workflows. Use the templates as a foundation and refine the prompts to match how you actually advise your clients.
          </Typography>
        </Alert>
      </Collapse>
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.5, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        {REPORT_TEMPLATES.map((t) => (
          <Chip
            key={t.key}
            label={t.label}
            size="small"
            variant={searchQuery === '' ? 'outlined' : 'outlined'}
            onClick={() => { if (!disabled) onTemplateSelect(t.prompt, t.key); }}
            disabled={disabled}
            sx={{
              fontWeight: 500,
              fontSize: '0.78rem',
              borderColor: 'primary.200',
              color: 'primary.dark',
              cursor: disabled ? 'default' : 'pointer',
              '&:hover': disabled ? {} : { bgcolor: 'primary.50', borderColor: 'primary.main' },
            }}
          />
        ))}
      </Box>

            {/* ── Filter History ── */}
      <Box display="flex" alignItems="center" gap={0.5} sx={{ mb: 0.75 }}>
        <Typography variant="caption" fontWeight={600} color="text.secondary"
          sx={{ textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.06em' }}>
          Filter History
        </Typography>
        <IconButton size="small" onClick={() => setShowFilterInfo(prev => !prev)} sx={{ p: 0.25 }}>
          <InfoIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
        </IconButton>
      </Box>
      <Collapse in={showFilterInfo}>
        <Alert severity="info" sx={{ mb: 1, py: 0.5 }} onClose={() => setShowFilterInfo(false)}>
          <Typography variant="body2" sx={{ mb: 0.75 }}>
            <strong>Search your full analysis history.</strong> The search runs against the complete text of every analysis — not just titles. If a concept, number, or client situation appears anywhere in an output, search will find it.
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.75 }}>
            <strong>Date filters</strong> narrow the history to a specific time window. By default, the panel shows the last 90 days. Use "Show all history" to see the full engagement record, or set From / To dates to focus on a specific period.
          </Typography>
          <Typography variant="body2">
            <strong>Sort options:</strong> Newest First (default), Oldest First, or Starred First. Star any analysis from the history panel to mark it for quick reference.
          </Typography>
        </Alert>
      </Collapse>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
        <TextField
          size="small"
          placeholder="Search and press Enter…"
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          onKeyDown={handleKeyDown}
          sx={{ flexGrow: 1, minWidth: 180, '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
              </InputAdornment>
            ),
            endAdornment: localSearch ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClear}>
                  <ClearIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <Button
          variant="contained"
          size="small"
          onClick={handleSearch}
          disabled={!localSearch.trim()}
          sx={{ height: 36, minWidth: 72, fontSize: '0.8rem' }}
        >
          Search
        </Button>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel sx={{ fontSize: '0.85rem' }}>Sort by</InputLabel>
          <Select
            value={sortOption}
            label="Sort by"
            onChange={(e) => onSortChange(e.target.value)}
            sx={{ fontSize: '0.85rem' }}
          >
            <MenuItem value="newest" sx={{ fontSize: '0.85rem' }}>Newest First</MenuItem>
            <MenuItem value="oldest" sx={{ fontSize: '0.85rem' }}>Oldest First</MenuItem>
            <MenuItem value="starred" sx={{ fontSize: '0.85rem' }}>Starred First</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          type="date"
          size="small"
          label="From"
          value={dateFrom || ''}
          onChange={(e) => onDateFromChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 148, '& .MuiInputBase-input': { fontSize: '0.83rem' } }}
        />
        <TextField
          type="date"
          size="small"
          label="To"
          value={dateTo || ''}
          onChange={(e) => onDateToChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 148, '& .MuiInputBase-input': { fontSize: '0.83rem' } }}
        />
        {(dateFrom || dateTo) && (
          <Button
            variant="text"
            size="small"
            onClick={onClearDates}
            sx={{ fontSize: '0.78rem', color: 'text.secondary', minWidth: 0, px: 0.5 }}
          >
            Clear dates
          </Button>
        )}

        <Box sx={{ flexGrow: 1 }} />

        {(searchQuery || dateFrom || dateTo) && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.78rem' }}>
            {filteredCount} {filteredCount === 1 ? 'item' : 'items'} found
          </Typography>
        )}

        <Button
          variant="text"
          size="small"
          onClick={onToggleShowAllHistory}
          sx={{ fontSize: '0.78rem', color: 'primary.main', minWidth: 0, px: 0.5 }}
        >
          {showAllHistory ? 'Last 90 days' : 'Show all history'}
        </Button>
      </Box>

      {/* ── My Saved Reports ── */}
      <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1.25, mt: 1.25 }}>
        <Box display="flex" alignItems="center" gap={0.5} sx={{ mb: 0.75 }}>
          <Typography
            variant="caption"
            fontWeight={600}
            color="text.secondary"
            sx={{ textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.06em' }}
          >
            My Saved Reports
          </Typography>
          <IconButton size="small" onClick={() => setShowSavedInfo(prev => !prev)} sx={{ p: 0.25 }}>
            <InfoIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
          </IconButton>
        </Box>

        <Collapse in={showSavedInfo}>
          <Alert severity="info" sx={{ mb: 1, py: 0.5 }} onClose={() => setShowSavedInfo(false)}>
            <Typography variant="body2" sx={{ mb: 0.75 }}>
              <strong>Save your best prompts.</strong> When you craft a prompt that works well for a specific type of client situation, save it here for reuse. Saved prompts appear as pills — click to pre-fill the input, then customize before running.
            </Typography>
            <Typography variant="body2">
              <strong>These are yours across all clients.</strong> Saved prompts are user-level, not client-specific — they appear for every client you work with. Over time, this becomes your personal advisory workflow library.
            </Typography>
          </Alert>
        </Collapse>

        {savedPrompts.length === 0 ? (
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ display: 'block', mb: 0.75, fontSize: '0.75rem' }}
          >
            No saved reports yet. Type a prompt in the input, then save it using the button below.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 0.75 }}>
            {savedPrompts.map((entry) => (
              <Box key={entry.savedAt} sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <Chip
                  label={entry.label}
                  size="small"
                  variant="outlined"
                  onClick={() => { if (!disabled) onTemplateSelect(entry.prompt, 'freeform'); }}
                  disabled={disabled}
                  sx={{
                    fontWeight: 500,
                    fontSize: '0.78rem',
                    borderColor: 'secondary.300',
                    color: 'secondary.dark',
                    cursor: disabled ? 'default' : 'pointer',
                    '&:hover': disabled ? {} : { bgcolor: 'secondary.50', borderColor: 'secondary.main' },
                  }}
                />
                <Tooltip title="Delete saved report">
                  <IconButton size="small" onClick={() => handleDeletePrompt(entry)} sx={{ p: 0.25 }}>
                    <DeleteIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Box>
        )}

        {showSaveInput ? (
          <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', mt: 0.5 }}>
            <TextField
              size="small"
              placeholder="Name this report…"
              value={saveLabel}
              onChange={(e) => setSaveLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSavePrompt();
                }
              }}
              sx={{ flexGrow: 1, '& .MuiInputBase-input': { fontSize: '0.83rem' } }}
            />
            <Button
              size="small"
              variant="contained"
              onClick={handleSavePrompt}
              disabled={!saveLabel.trim() || !currentPrompt?.trim()}
              sx={{ fontSize: '0.78rem', height: 32 }}
            >
              Save
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={() => {
                setShowSaveInput(false);
                setSaveLabel('');
              }}
              sx={{ fontSize: '0.78rem', height: 32, color: 'text.secondary' }}
            >
              Cancel
            </Button>
          </Box>
        ) : (
          <Button
            size="small"
            variant="outlined"
            startIcon={<SaveIcon sx={{ fontSize: 14 }} />}
            onClick={() => setShowSaveInput(true)}
            disabled={!currentPrompt?.trim()}
            sx={{ fontSize: '0.78rem', height: 30, mt: 0.25 }}
          >
            Save current prompt
          </Button>
        )}
      </Box>
    </Paper>
  );
};

export default FIBottomPanel;